import type { ActionDefinition } from '@command-center/action-sdk';

export type DataSourceType = 'airtable' | 'supabase' | 'custom' | 'mock';
export type FieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'url'
  | 'email'
  | 'select'
  | 'multi-select'
  | 'relation'
  | 'currency'
  | 'attachment'
  | 'markdown'
  | 'json';

export type RecordDetailPresentation = 'drawer' | 'centerPane' | 'fullScreen';

export interface RecordVisibilityRules {
  /** Hide the record or field unconditionally without deleting its configuration. */
  hidden?: boolean;
  /** At least one matching role is required when roles are configured. */
  roles?: string[];
  /** Every configured permission is required. */
  permissions?: string[];
  /** Human-readable explanation shown by configuration review surfaces. */
  description?: string;
}

export interface RecordAccessContext {
  roles?: string[];
  permissions?: string[];
  /** Server-resolved effective capability ceiling for the current resource scope. */
  capabilities?: Partial<RecordTypeCapabilities>;
}

export type RecordFieldDisplayControl =
  | 'text'
  | 'badge'
  | 'code'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'duration'
  | 'email'
  | 'key-value'
  | 'image'
  | 'link'
  | 'list'
  | 'markdown'
  | 'phone';

export type RecordFieldInputControl =
  | 'text'
  | 'textarea'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multi-select'
  | 'relation-picker'
  | 'attachment'
  | 'image-upload'
  | 'markdown';

/** Provider-neutral linked-record metadata used by forms, API services, and Alice. */
export interface RecordRelationDefinition {
  sourceTable: string;
  titleField: string;
  /** Registry key for the related resource when it is exposed by this portal. */
  recordTypeKey?: string;
  hrefBase?: string;
  /** Airtable links are arrays; false permits a single normalized id in custom adapters. */
  multiple?: boolean;
  minItems?: number;
  maxItems?: number;
}

/** Constraints applied before an attachment value is handed to any provider adapter. */
export interface RecordAttachmentDefinition {
  /** MIME types (`image/png`) or families (`image/*`). Empty means any type. */
  accept?: string[];
  maxBytes?: number;
  maxFiles?: number;
  kind?: 'image' | 'document' | 'any';
  /** Visual hint only; uploads still use the configured portal upload callback. */
  presentation?: 'default' | 'logo';
}

/** JSON-safe attachment reference accepted by registry mutation services. */
export interface RecordAttachmentValue {
  /** Provider attachment id when one exists (for exact remove/download actions). */
  id?: string;
  url: string;
  filename?: string;
  type?: string;
  size?: number;
}

/**
 * One app-facing field in a registered record type.
 *
 * `key` is the normalized property path used by portal DTOs. `sourceField`
 * records the existing Airtable/Supabase field and is descriptive in this
 * pass: registry creation never mutates a provider schema.
 */
export interface RecordFieldDefinition {
  key: string;
  label: string;
  sourceField: string;
  type: FieldType;
  readOnly: boolean;
  editable: boolean;
  /** Canonical write flag. Omit to inherit the legacy `editable` value. */
  writable?: boolean;
  /** Required on create. `requiredOnCreate` is the explicit, preferred spelling. */
  required?: boolean;
  requiredOnCreate?: boolean;
  visibleInDetails: boolean;
  availableInCreate: boolean;
  availableInEdit: boolean;
  searchable: boolean;
  filterable: boolean;
  sortable: boolean;
  visibility?: RecordVisibilityRules;
  displayControl?: RecordFieldDisplayControl;
  inputControl?: RecordFieldInputControl;
  /** Short, stable choices a generic registry list may expose as a filter. */
  filterOptions?: string[];
  /** Provider relationship metadata used for bounded, lazy linked-record reads. */
  relationship?: RecordRelationDefinition;
  /** Present only for attachment fields. */
  attachment?: RecordAttachmentDefinition;
}

export interface RecordListPresentation {
  /** Context rendered under the title in compact lists, cards, and tables. */
  subtitleFields?: string[];
  /** Select/state fields rendered as badges. */
  badgeFields?: string[];
  /** One explanatory sentence shown prominently on cards. */
  headlineField?: string;
  /** Field keys rendered after the identity column in the standard table. */
  tableFields: string[];
  /** Field keys rendered in the standard card fact grid. */
  cardFields: string[];
  /** Default provider-side sort for bounded list reads. */
  defaultSort?: { field: string; direction: 'asc' | 'desc' };
  /** Primary filter shown beside search when the field is filterable. */
  primaryFilterField?: string;
}

export interface RecordDetailTabDefinition {
  id: string;
  label: string;
  /** Registry field keys rendered in this tab. */
  fieldKeys?: string[];
  /** Relationship field keys loaded only while this tab is active. */
  relationshipKeys?: string[];
}

export interface RegisteredRecordPresentation {
  list: RecordListPresentation;
  detailTabs: RecordDetailTabDefinition[];
}

export interface RecordTypeCapabilities {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
}

export interface RecordTypeDefinition {
  key: string;
  singularLabel: string;
  pluralLabel: string;
  /** Lucide icon name resolved by the consuming portal. */
  icon: string;
  source: {
    type: DataSourceType;
    /** Existing table, view, or bounded service exposed by the app. */
    name: string;
    connectorId?: string;
  };
  primaryKey: string;
  titleField: string;
  imageField?: string;
  capabilities: RecordTypeCapabilities;
  defaultDetailPresentation: RecordDetailPresentation;
  supportsFavorites: boolean;
  supportsBookmarks: boolean;
  visibility?: RecordVisibilityRules;
  /** Shared list/card/detail hints consumed by registry-driven workspaces. */
  presentation?: RegisteredRecordPresentation;
  fields: RecordFieldDefinition[];
}

export interface RecordTypeRegistry {
  all: () => RecordTypeDefinition[];
  get: (key: string) => RecordTypeDefinition | undefined;
  register: (definition: RecordTypeDefinition) => RecordTypeDefinition;
}

export function createRecordTypeDefinition(
  definition: RecordTypeDefinition,
): RecordTypeDefinition {
  if (!definition.key.trim()) throw new Error('Record type key cannot be empty.');
  if (!definition.singularLabel.trim() || !definition.pluralLabel.trim()) {
    throw new Error(`Record type ${definition.key} must define singular and plural labels.`);
  }
  if (!definition.source.name.trim()) {
    throw new Error(`Record type ${definition.key} must define an existing source name.`);
  }
  if (definition.fields.length === 0) {
    throw new Error(`Record type ${definition.key} must expose at least one field.`);
  }

  const fieldKeys = new Set<string>();
  for (const field of definition.fields) {
    if (!field.key.trim()) throw new Error(`Record type ${definition.key} has an empty field key.`);
    if (fieldKeys.has(field.key)) {
      throw new Error(`Duplicate field key in ${definition.key}: ${field.key}`);
    }
    if (field.readOnly && field.editable) {
      throw new Error(`Record field ${definition.key}.${field.key} cannot be read-only and editable.`);
    }
    if (field.readOnly && field.writable) {
      throw new Error(`Record field ${definition.key}.${field.key} cannot be read-only and writable.`);
    }
    if (field.readOnly && (field.availableInCreate || field.availableInEdit)) {
      throw new Error(`Read-only record field ${definition.key}.${field.key} cannot appear in forms.`);
    }
    if ((field.required || field.requiredOnCreate) && !field.availableInCreate) {
      throw new Error(`Required record field ${definition.key}.${field.key} must be available on create.`);
    }
    if ((field.required || field.requiredOnCreate) && !recordFieldIsWritable(field)) {
      throw new Error(`Required record field ${definition.key}.${field.key} must be writable.`);
    }
    // Existing registries contain read-only and legacy-editable relation fields
    // that predate provider-neutral relationship metadata. Keep those definitions
    // loadable; new mutation-aware relations opt in with the explicit writable
    // flag and must declare an allowlisted target.
    if (field.type === 'relation' && field.writable === true && !field.relationship) {
      throw new Error(`Relation field ${definition.key}.${field.key} must define relationship metadata.`);
    }
    if (field.type !== 'relation' && field.relationship) {
      throw new Error(`Non-relation field ${definition.key}.${field.key} cannot define relationship metadata.`);
    }
    if (field.type !== 'attachment' && field.attachment) {
      throw new Error(`Non-attachment field ${definition.key}.${field.key} cannot define attachment metadata.`);
    }
    if (field.attachment?.maxBytes !== undefined && field.attachment.maxBytes <= 0) {
      throw new Error(`Attachment field ${definition.key}.${field.key} must use a positive maxBytes.`);
    }
    if (
      field.attachment?.maxFiles !== undefined &&
      (!Number.isInteger(field.attachment.maxFiles) || field.attachment.maxFiles <= 0)
    ) {
      throw new Error(`Attachment field ${definition.key}.${field.key} must use a positive maxFiles.`);
    }
    for (const [name, count] of [
      ['minItems', field.relationship?.minItems],
      ['maxItems', field.relationship?.maxItems],
    ] as const) {
      if (count !== undefined && (!Number.isInteger(count) || count < 0)) {
        throw new Error(`Relation field ${definition.key}.${field.key} must use a non-negative ${name}.`);
      }
    }
    if (
      field.relationship?.minItems !== undefined &&
      field.relationship.maxItems !== undefined &&
      field.relationship.minItems > field.relationship.maxItems
    ) {
      throw new Error(`Relation field ${definition.key}.${field.key} has minItems above maxItems.`);
    }
    fieldKeys.add(field.key);
  }

  for (const key of [definition.primaryKey, definition.titleField, definition.imageField]) {
    if (key && !fieldKeys.has(key)) {
      throw new Error(`Record type ${definition.key} references an unregistered field: ${key}`);
    }
  }

  const presentationFieldKeys = new Set<string>();
  const presentation = definition.presentation;
  if (presentation) {
    for (const key of [
      ...(presentation.list.subtitleFields ?? []),
      ...(presentation.list.badgeFields ?? []),
      ...(presentation.list.headlineField ? [presentation.list.headlineField] : []),
      ...presentation.list.tableFields,
      ...presentation.list.cardFields,
      ...(presentation.list.defaultSort ? [presentation.list.defaultSort.field] : []),
      ...(presentation.list.primaryFilterField ? [presentation.list.primaryFilterField] : []),
      ...presentation.detailTabs.flatMap((tab) => [
        ...(tab.fieldKeys ?? []),
        ...(tab.relationshipKeys ?? []),
      ]),
    ]) presentationFieldKeys.add(key);

    for (const key of presentationFieldKeys) {
      if (!fieldKeys.has(key)) {
        throw new Error(`Record type ${definition.key} presentation references unknown field: ${key}`);
      }
    }
  }

  return definition;
}

export function createRecordTypeRegistry(
  definitions: RecordTypeDefinition[] = [],
): RecordTypeRegistry {
  const entries = new Map<string, RecordTypeDefinition>();

  const register = (definition: RecordTypeDefinition) => {
    const validated = createRecordTypeDefinition(definition);
    if (entries.has(validated.key)) {
      throw new Error(`Record type already registered: ${validated.key}`);
    }
    entries.set(validated.key, validated);
    return validated;
  };

  definitions.forEach(register);
  return {
    all: () => Array.from(entries.values()),
    get: (key) => entries.get(key),
    register,
  };
}

export function recordVisibilityAllows(
  rules: RecordVisibilityRules | undefined,
  access: RecordAccessContext = {},
): boolean {
  if (rules?.hidden) return false;
  if (rules?.roles?.length) {
    const roles = new Set(access.roles ?? []);
    if (!rules.roles.some((role) => roles.has(role))) return false;
  }
  if (rules?.permissions?.length) {
    const permissions = new Set(access.permissions ?? []);
    if (!rules.permissions.every((permission) => permissions.has(permission))) return false;
  }
  return true;
}

export function recordCapabilitiesForAccess(
  configured: RecordTypeCapabilities,
  access: RecordAccessContext = {},
): RecordTypeCapabilities {
  const roles = new Set((access.roles ?? []).map((role) => role.toLowerCase()));
  const elevated = ['owner', 'admin', 'administrator'].some((role) => roles.has(role));
  const viewerOnly = roles.has('viewer') && !elevated;
  const member = roles.has('member') && !elevated;
  const ceiling = access.capabilities;
  return {
    view: configured.view && (ceiling?.view ?? true),
    create: configured.create && !viewerOnly && (ceiling?.create ?? true),
    edit: configured.edit && !viewerOnly && (ceiling?.edit ?? true),
    delete: configured.delete && !viewerOnly && !member && (ceiling?.delete ?? true),
  };
}

/** One compatibility rule for old registry definitions and new CRUD-aware ones. */
export function recordFieldIsWritable(field: RecordFieldDefinition): boolean {
  return !field.readOnly && (field.writable ?? field.editable);
}

export function recordFieldIsRequiredOnCreate(field: RecordFieldDefinition): boolean {
  return Boolean(field.requiredOnCreate ?? field.required);
}

export interface FieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  sourceField?: string;
  required?: boolean;
  readonly?: boolean;
  options?: Array<{ label: string; value: string; color?: string }>;
}

export interface FilterDefinition {
  id: string;
  label: string;
  fieldId: string;
  operator: 'equals' | 'not-equals' | 'contains' | 'starts-with' | 'is-empty' | 'is-not-empty';
  value?: unknown;
}

export interface DetailViewDefinition {
  titleFieldId?: string;
  sections: Array<{
    id: string;
    label: string;
    fieldIds: string[];
  }>;
}

export interface TableDefinition {
  id: string;
  label: string;
  source: DataSourceType;
  connectorId?: string;
  tableName?: string;
  baseIdEnv?: string;
  defaultView?: string;
  fields: FieldDefinition[];
  filters?: FilterDefinition[];
  rowActions?: ActionDefinition[];
  detailView?: DetailViewDefinition;
}

export interface ViewDefinition {
  id: string;
  label: string;
  tableId: string;
  filters?: FilterDefinition[];
  visibleFieldIds?: string[];
  sort?: Array<{ fieldId: string; direction: 'asc' | 'desc' }>;
}

export interface DataAdapter<TRecord = Record<string, unknown>> {
  source: DataSourceType;
  list: (table: TableDefinition, query?: DataQuery) => Promise<DataPage<TRecord>>;
  get?: (table: TableDefinition, id: string) => Promise<TRecord | null>;
  create?: (table: TableDefinition, input: DataMutationInput) => Promise<TRecord>;
  update?: (table: TableDefinition, id: string, input: DataMutationInput) => Promise<TRecord>;
  delete?: (table: TableDefinition, id: string) => Promise<DataDeleteResult>;
}

export interface DataMutationInput {
  fields: Record<string, unknown>;
}

export interface DataDeleteResult {
  id: string;
  deleted: boolean;
}

export interface DataQuery {
  search?: string;
  filters?: Record<string, unknown>;
  cursor?: string;
  limit?: number;
}

export interface DataPage<TRecord = Record<string, unknown>> {
  items: TRecord[];
  nextCursor?: string | null;
  total?: number;
}

export function createTableDefinition(table: TableDefinition): TableDefinition {
  if (!table.id.trim()) throw new Error('Table id cannot be empty.');
  if (table.fields.length === 0) throw new Error(`Table ${table.id} must define at least one field.`);
  return table;
}

export function createViewDefinition(view: ViewDefinition): ViewDefinition {
  if (!view.id.trim()) throw new Error('View id cannot be empty.');
  return view;
}
